/**
 * Everything the combat sim produces, made visible: targets, missiles, wreckage, and the effects
 * they trigger.
 *
 * ## One place that owns the sim → render boundary
 *
 * Positions in `game/` and `aircraft/weapons/` are sim-frame NED, in double precision, relative to
 * the floating origin. Positions in the scene graph are render space — y up, nose down −Z, FP32,
 * with the curvature drop applied. Exactly one function converts between them
 * (`FlightScene.simNedToRender`), and this module is the only consumer of it for combat objects.
 * Everything downstream of here is renderer; everything upstream is simulation, and nothing in
 * `game/` or `weapons/` imports three beyond `Vector3`.
 *
 * ## The meshes are primitives, deliberately
 *
 * Blocked-in shapes, like the stand-in airframe. A target drone is a fuselage, a wing and a fin;
 * a missile is a body and four fins. Modelling them properly is asset work, and the milestone this
 * belongs to is about whether the *weapons* work — a beautifully modelled drone that the gun
 * cannot hit would be the wrong thing to have built. They are separate objects with their own
 * materials, so replacing them later is replacing one function.
 *
 * ## Wreckage, and the parachute
 *
 * A killed drone is not deleted. It keeps its transform, stops flying, tumbles, smokes, and a
 * parachute opens above it after a beat — which is what actually happens to a target drone,
 * because they are expensive and get recovered and used again. It is also much better feedback
 * than a puff and an absence: you can see what you did.
 *
 * `updateFrame(alpha, dt)`: yes — {@link CombatView.updateFrame}. Allocation-free after
 * construction; every mesh exists from the moment its target does.
 */

import {
  BoxGeometry,
  BufferGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  SphereGeometry,
  Vector3,
  type Material,
} from 'three';
import { MeshStandardNodeMaterial } from 'three/webgpu';
import type { MissileBattery } from '../aircraft/weapons/missile.js';
import type { Cannon } from '../aircraft/weapons/cannon.js';
import type { HitQueue } from '../aircraft/weapons/engagement.js';
import type { Target } from '../game/targets.js';
import type { CombatEffects } from './effects/combatEffects.js';

/** Converts a sim-frame NED position into render space. `FlightScene.simNedToRender`. */
export type NedToRender = (positionNed: Vector3, out: Vector3) => Vector3;

const scratch = /* #__PURE__ */ new Vector3();
const scratchVelocity = /* #__PURE__ */ new Vector3();
const scratchTail = /* #__PURE__ */ new Vector3();

/** One target's visual representation. */
interface TargetVisual {
  group: Group;
  /** The airframe or the envelope — hidden when the tether is all that is left. */
  body: Group;
  chute: Mesh | null;
  tether: Mesh | null;
  targetId: number;
}

/** One missile in the air. */
interface MissileVisual {
  group: Group;
  /** Which pooled round it is showing, or −1. */
  index: number;
}

export class CombatView {
  readonly group = new Group();

  private readonly effects: CombatEffects;
  private readonly toRender: NedToRender;
  private readonly visuals: TargetVisual[] = [];
  private readonly missiles: MissileVisual[] = [];
  private readonly geometries: BufferGeometry[] = [];
  private readonly materials: Material[] = [];

  /** Reused: the render-space position of whatever is being placed this call. */
  private readonly renderPosition = new Vector3();
  private readonly previousRender = new Vector3();

  constructor(options: { effects: CombatEffects; toRender: NedToRender }) {
    this.effects = options.effects;
    this.toRender = options.toRender;
    this.group.name = 'combat';
  }

  private track<T extends BufferGeometry>(geometry: T): T {
    this.geometries.push(geometry);
    return geometry;
  }

  private trackMaterial<T extends Material>(material: T): T {
    this.materials.push(material);
    return material;
  }

  /**
   * Build the meshes for a course.
   *
   * Called once when a course starts, not per frame — a target's visual exists for exactly as long
   * as the target does, so there is nothing to add or remove while the run is going on.
   */
  buildTargets(targets: readonly Target[]): void {
    this.clearTargets();
    for (const target of targets) {
      const visual =
        target.kind === 'balloon' ? this.buildBalloon(target) : this.buildDrone(target);
      this.visuals.push(visual);
      this.group.add(visual.group);
    }
  }

  /** Build the missile meshes. Called once, from the battery's pool size. */
  buildMissiles(battery: MissileBattery): void {
    for (const visual of this.missiles) this.group.remove(visual.group);
    this.missiles.length = 0;

    const bodyMaterial = this.trackMaterial(new MeshStandardNodeMaterial());
    bodyMaterial.color = new Color(0xd8dade);
    bodyMaterial.roughness = 0.5;
    bodyMaterial.metalness = 0.3;

    const bodyGeometry = this.track(new CylinderGeometry(0.09, 0.09, 2.9, 10));
    const noseGeometry = this.track(new ConeGeometry(0.09, 0.5, 10));
    const finGeometry = this.track(new BoxGeometry(0.02, 0.34, 0.3));

    for (let i = 0; i < battery.rounds.length; i++) {
      const group = new Group();
      const body = new Mesh(bodyGeometry, bodyMaterial);
      // Render space: the model is built along −Z, so the cylinder's +Y axis is tipped forward.
      body.rotation.x = Math.PI / 2;
      group.add(body);

      const nose = new Mesh(noseGeometry, bodyMaterial);
      nose.rotation.x = -Math.PI / 2;
      nose.position.set(0, 0, -1.7);
      group.add(nose);

      for (const angle of [0, Math.PI / 2, Math.PI, -Math.PI / 2]) {
        const fin = new Mesh(finGeometry, bodyMaterial);
        fin.position.set(Math.sin(angle) * 0.16, Math.cos(angle) * 0.16, 1.25);
        fin.rotation.z = -angle;
        group.add(fin);
      }

      group.visible = false;
      this.group.add(group);
      this.missiles.push({ group, index: i });
    }
  }

  private buildDrone(target: Target): TargetVisual {
    const group = new Group();
    const body = new Group();
    group.add(body);

    const shell = this.trackMaterial(new MeshStandardNodeMaterial());
    // Target drones are painted to be seen — international orange, and it is not a stylistic
    // choice: a target you cannot pick out against terrain is a test of the terrain.
    shell.color = new Color(target.kind === 'evasive' ? 0xff5a1f : 0xff8a2b);
    shell.roughness = 0.55;
    shell.metalness = 0.1;

    const dark = this.trackMaterial(new MeshStandardNodeMaterial());
    dark.color = new Color(0x16191e);
    dark.roughness = 0.7;

    const fuselage = new Mesh(this.track(new CylinderGeometry(0.42, 0.3, 5.2, 12)), shell);
    fuselage.rotation.x = Math.PI / 2;
    fuselage.castShadow = true;
    body.add(fuselage);

    const nose = new Mesh(this.track(new ConeGeometry(0.42, 1.1, 12)), shell);
    nose.rotation.x = -Math.PI / 2;
    nose.position.set(0, 0, -3.05);
    body.add(nose);

    const wing = new Mesh(this.track(new BoxGeometry(5.6, 0.12, 0.95)), shell);
    wing.position.set(0, 0.1, 0.2);
    wing.castShadow = true;
    body.add(wing);

    const tailplane = new Mesh(this.track(new BoxGeometry(2.1, 0.1, 0.55)), shell);
    tailplane.position.set(0, 0.15, 2.4);
    body.add(tailplane);

    const fin = new Mesh(this.track(new BoxGeometry(0.1, 1.0, 0.8)), dark);
    fin.position.set(0, 0.65, 2.5);
    body.add(fin);

    // The recovery parachute, folded until it is needed.
    const chuteMaterial = this.trackMaterial(new MeshStandardNodeMaterial());
    chuteMaterial.color = new Color(0xf2f4f7);
    chuteMaterial.roughness = 0.9;
    const chute = new Mesh(
      this.track(new SphereGeometry(2.6, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.5)),
      chuteMaterial,
    );
    chute.position.set(0, 5.5, 0);
    chute.visible = false;
    group.add(chute);

    return { group, body, chute, tether: null, targetId: target.id };
  }

  private buildBalloon(target: Target): TargetVisual {
    const group = new Group();
    const body = new Group();
    group.add(body);

    const skin = this.trackMaterial(new MeshStandardNodeMaterial());
    skin.color = new Color(0xff2d55);
    skin.roughness = 0.35;
    skin.metalness = 0;

    const envelope = new Mesh(this.track(new SphereGeometry(target.radiusM, 16, 12)), skin);
    envelope.scale.set(1, 1.25, 1);
    envelope.castShadow = true;
    body.add(envelope);

    /*
     * The tether, and why it is drawn at all.
     *
     * A balloon floating with nothing holding it down reads as a beach ball hanging in the air.
     * The line to the ground is what makes it a *tethered* balloon, and it is also the only thing
     * in the canyon that gives the eye a vertical reference at a hundred feet.
     */
    const cordMaterial = this.trackMaterial(new MeshStandardNodeMaterial());
    cordMaterial.color = new Color(0x2a2a2a);
    cordMaterial.roughness = 1;
    const tether = new Mesh(this.track(new CylinderGeometry(0.06, 0.06, 1, 5)), cordMaterial);
    group.add(tether);

    return { group, body, chute: null, tether, targetId: target.id };
  }

  /**
   * Place everything and fire off whatever effects this frame's events call for.
   *
   * @param heightAboveAnchorM How high each balloon rides above its tether point [m] — the mode
   *   knows the ground, this does not.
   */
  updateFrame(
    dt: number,
    targets: readonly Target[],
    battery: MissileBattery | null,
    cannon: Cannon | null,
    hits: HitQueue,
  ): void {
    this.updateTargets(dt, targets);
    if (battery !== null) this.updateMissiles(dt, battery);
    if (cannon !== null) this.updateTracers(cannon);
    this.updateHits(hits);
  }

  private updateTargets(dt: number, targets: readonly Target[]): void {
    for (let i = 0; i < this.visuals.length && i < targets.length; i++) {
      const visual = this.visuals[i];
      const target = targets[i];

      this.toRender(target.positionNed, this.renderPosition);
      visual.group.position.copy(this.renderPosition);

      if (target.kind === 'balloon') {
        // Stretch the tether from the ground to wherever the envelope has bobbed to.
        const tether = visual.tether;
        if (tether !== null) {
          const height = Math.max(1, target.localNed.z * -1);
          tether.scale.set(1, height, 1);
          tether.position.set(0, -height * 0.5, 0);
          tether.visible = target.alive;
        }
        visual.body.visible = target.alive;
        continue;
      }

      // Render space: heading is a rotation about the vertical, and render y is up, so a NED
      // heading of 0 (north) is −Z. Bank is a roll about the nose.
      visual.group.rotation.set(0, -target.headingRad, 0, 'YXZ');
      visual.body.rotation.set(0, 0, target.bankRad);
      visual.body.visible = true;

      if (target.alive) {
        // A drone that has taken a beating trails smoke before it dies.
        if (target.health < 0.55) {
          scratchVelocity.set(0, 0, 0);
          this.effects.damageSmoke(this.renderPosition, scratchVelocity, (1 - target.health) * 26, dt);
        }
        if (visual.chute !== null) visual.chute.visible = false;
        continue;
      }

      // --- dead ---------------------------------------------------------------------------
      if (target.justDied) this.effects.explosion(this.renderPosition, 1);
      scratchVelocity.set(0, 0, 0);
      this.effects.damageSmoke(this.renderPosition, scratchVelocity, 34, dt);
      /*
       * The chute opens after a beat rather than instantly. A recovery chute is fired by a timer
       * once the airframe has stopped tumbling, and the pause is what makes the tumble readable —
       * open it on the same frame as the explosion and the two events merge into one.
       */
      if (visual.chute !== null) visual.chute.visible = target.deadForS > 1.1;
    }
  }

  private updateMissiles(dt: number, battery: MissileBattery): void {
    for (let i = 0; i < this.missiles.length && i < battery.rounds.length; i++) {
      const visual = this.missiles[i];
      const round = battery.rounds[i];
      if (!round.active) {
        visual.group.visible = false;
        continue;
      }

      this.toRender(round.positionNed, this.renderPosition);
      visual.group.visible = true;
      visual.group.position.copy(this.renderPosition);

      /*
       * Point it along its own velocity.
       *
       * `lookAt` wants a world-space point, so the target is the missile's position plus its
       * axis. A missile flies very nearly along its axis — the angle of attack that produces its
       * lift is a couple of degrees — so this is both simple and right.
       */
      scratch.copy(round.axisNed);
      scratchTail.set(
        round.positionNed.x + scratch.x,
        round.positionNed.y + scratch.y,
        round.positionNed.z + scratch.z,
      );
      this.toRender(scratchTail, this.previousRender);
      visual.group.lookAt(this.previousRender);

      // The trail comes off the tail, not the centre of the model.
      scratch.subVectors(this.previousRender, this.renderPosition).normalize();
      scratchTail.copy(this.renderPosition).addScaledVector(scratch, -1.6);
      scratchVelocity.copy(scratch).multiplyScalar(-40);
      this.effects.missileTrail(scratchTail, scratchVelocity, round.motorTimeS > 0, dt);
    }

    // Detonations that happened this tick.
    for (let i = 0; i < battery.detonationLength; i++) {
      const event = battery.detonations[i];
      this.toRender(event.positionNed, this.renderPosition);
      this.effects.explosion(this.renderPosition, event.reason === 'timeout' ? 0.6 : 1.2);
    }
  }

  /**
   * Draw the tracers.
   *
   * Only the rounds flagged as tracers are drawn — the other four in five are invisible, which is
   * the whole point of a tracer being every fifth round. Drawing all of them would produce a solid
   * bar of light and would also be five times the fill.
   */
  private updateTracers(cannon: Cannon): void {
    const ribbons = this.effects.tracers;
    let written = 0;
    for (let i = 0; i < cannon.rounds.length && written < ribbons.capacity; i++) {
      const round = cannon.rounds[i];
      if (!round.active || !round.tracer) continue;

      this.toRender(round.positionNed, this.renderPosition);
      // The velocity has to be rotated into render space too, and it is a *direction*, so it is
      // differenced from two converted points rather than converted directly.
      scratchTail.set(
        round.positionNed.x + round.velocityNed.x,
        round.positionNed.y + round.velocityNed.y,
        round.positionNed.z + round.velocityNed.z,
      );
      this.toRender(scratchTail, this.previousRender);
      scratchVelocity.subVectors(this.previousRender, this.renderPosition);

      /*
       * Tracers burn out. The composition is spent over the round's flight, so an old tracer is
       * dimmer than a fresh one, and a stream fades out at its far end rather than stopping dead.
       */
      const burn = Math.max(0, 1 - round.ageS / 2.4);
      ribbons.set(
        written,
        this.renderPosition.x,
        this.renderPosition.y,
        this.renderPosition.z,
        scratchVelocity.x,
        scratchVelocity.y,
        scratchVelocity.z,
        6.0 * burn,
        1.8 * burn,
        0.35 * burn,
        burn,
        0.16,
      );
      written++;
    }
    ribbons.commit(written);
  }

  /** Sparks where things were hit this tick. */
  private updateHits(hits: HitQueue): void {
    for (let i = 0; i < hits.length; i++) {
      const report = hits.at(i);
      if (report.kind !== 'cannon') continue;
      this.toRender(report.positionNed, this.renderPosition);
      scratchTail.set(
        report.positionNed.x + report.directionNed.x,
        report.positionNed.y + report.directionNed.y,
        report.positionNed.z + report.directionNed.z,
      );
      this.toRender(scratchTail, this.previousRender);
      scratchVelocity.subVectors(this.previousRender, this.renderPosition).normalize();
      this.effects.hitSparks(this.renderPosition, scratchVelocity, report.speedMps);
    }
  }

  /** Drop every target mesh — leaving a course, or starting a different one. */
  clearTargets(): void {
    for (const visual of this.visuals) this.group.remove(visual.group);
    this.visuals.length = 0;
  }

  dispose(): void {
    this.clearTargets();
    for (const visual of this.missiles) this.group.remove(visual.group);
    this.missiles.length = 0;
    for (const geometry of this.geometries) geometry.dispose();
    for (const material of this.materials) material.dispose();
  }
}
